import {
  Button,
  EmptyState,
  LoadingState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  toast,
  Tooltip,
} from '@portal/ui';
import { useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationPush } from '../contracts.js';
import { NotificationItems, useMarkRead } from './NotificationItems.js';
import { useNotifications, useNotificationStream, useUnreadCount } from './queries.js';

/** Topbar bell: unread badge, realtime toasts and the latest notifications in a side sheet. */
export function NotificationBell() {
  const { t } = useTranslation('core-notification');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount();
  const count = unread.data?.count ?? 0;

  const onNotification = useCallback(
    (n: NotificationPush) =>
      toast(n.title, {
        description: n.body.length > 140 ? `${n.body.slice(0, 140)}…` : n.body,
        action: n.link
          ? { label: t('open'), onClick: () => void navigate({ to: n.link! }) }
          : undefined,
      }),
    [navigate, t],
  );
  useNotificationStream(onNotification);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Tooltip content={t('title')}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="relative"
            aria-label={count > 0 ? `${t('title')} (${t('unreadCount', { count })})` : t('title')}
          >
            <Bell />
            {count > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-destructive-foreground tabular-nums">
                {count > 99 ? '99+' : count}
              </span>
            ) : null}
          </Button>
        </SheetTrigger>
      </Tooltip>
      <SheetContent className="flex flex-col gap-0 p-0">
        {open ? <Latest onClose={() => setOpen(false)} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function Latest({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('core-notification');
  const navigate = useNavigate();
  const list = useNotifications(false, 20);
  const markRead = useMarkRead();
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = list.data?.pages[0]?.unreadCount ?? 0;

  return (
    <>
      <SheetHeader className="border-b px-4 py-3">
        <SheetTitle>{t('title')}</SheetTitle>
        <SheetDescription>
          {unread > 0 ? t('unreadCount', { count: unread }) : t('description')}
        </SheetDescription>
      </SheetHeader>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isPending ? (
          <LoadingState rows={4} className="p-4" />
        ) : items.length === 0 ? (
          <EmptyState icon={Bell} title={t('empty')} className="m-4" />
        ) : (
          <NotificationItems items={items} onOpen={onClose} />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          disabled={unread === 0}
          onClick={() => markRead.mutate('all')}
        >
          {t('markAllRead')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            onClose();
            void navigate({ to: '/notifications' });
          }}
        >
          {t('viewAll')}
        </Button>
      </div>
    </>
  );
}
